/**
 * The map's `?show=` filter, in a module of its own.
 *
 * `/properties/map` shape-checks the URL in `validateSearch`, which runs in the eager route tree.
 * Rollup tree-shakes across the whole graph, not per chunk: `PropertyMapPage` uses every export of
 * `mapStyle.ts`, so importing this check from there put the entire basemap styling module in the
 * entry chunk. Same reason `located.ts` and `tabSearch.ts` stand alone.
 */

/** What the chips above the map select. Each one is a style change, never a reload. */
export const PROPERTY_MAP_FILTERS = ['all', 'let', 'vacant', 'listed', 'noncompliant'] as const;
export type PropertyMapFilter = (typeof PROPERTY_MAP_FILTERS)[number];

/**
 * Whether a string off the URL names a view this map has.
 *
 * The filter is in the query string now, so it arrives from bookmarks, from links people send
 * each other, and from URLs somebody edited by hand. A value we no longer recognise has to
 * degrade to the whole portfolio rather than reach `applyPropertyFilter`, where an unknown case
 * would fall through to a style expression that hides every pin.
 */
export function isPropertyMapFilter(value: string): value is PropertyMapFilter {
  return (PROPERTY_MAP_FILTERS as readonly string[]).includes(value);
}

/**
 * What the map is drawn AS — `?view=`. Pins is the default and is never written to the URL.
 *
 * `suburbs` swaps the pins for suburb boundaries shaded by how many properties the agency has in
 * each, with the road network hidden: the question changes from "where is this house" to "where
 * is our book, and what does it rent for there".
 *
 * `grids` is the same picture one size up: the agency's own areas, each a named collection of
 * suburbs (0127), with a suburb's figures pooled across the grid — and the place grids are drawn.
 */
export const PROPERTY_MAP_VIEWS = ['pins', 'suburbs', 'grids'] as const;
export type PropertyMapView = (typeof PROPERTY_MAP_VIEWS)[number];

export function isPropertyMapView(value: string): value is PropertyMapView {
  return (PROPERTY_MAP_VIEWS as readonly string[]).includes(value);
}

/**
 * How the suburb and grid views are coloured — `?colour=`. Standard is the default and is never
 * written to the URL.
 *
 *   standard   one blue, stronger where there are more properties
 *   heat       a ramp over ONE figure, chosen with `?metric=`
 *   separate   a colour apiece, so that no two neighbours share one — the colour says where one
 *              area stops and the next starts, and nothing else
 */
export const AREA_COLOURINGS = ['standard', 'heat', 'separate'] as const;
export type AreaColouring = (typeof AREA_COLOURINGS)[number];

export function isAreaColouring(value: string): value is AreaColouring {
  return (AREA_COLOURINGS as readonly string[]).includes(value);
}

/** What a heat map is a map OF — `?metric=`. `properties` is the default and is not written. */
export const HEAT_METRICS = ['properties', 'rent', 'vacancy', 'noncompliant', 'inspections'] as const;
export type HeatMetric = (typeof HEAT_METRICS)[number];

export function isHeatMetric(value: string): value is HeatMetric {
  return (HEAT_METRICS as readonly string[]).includes(value);
}
